/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useEffect, useState, useRef, useMemo } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import './styles.scss'

import { Link } from '@ta-interaktiv/contentbricks'
import { MetaInfos } from 'components/MetaInfos'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { ClusterCannon } from 'components/clusterCannon'
import scrollama from 'scrollama'
import { NameComparission } from 'components/charts/NameComparission'
import { NameInCulture } from 'components/charts/NameInCulture'
import { Top10 } from 'components/charts/Top10'
import { parseHostname } from '@ta-interaktiv/parse-hostname'
import { ArticleTeasers } from '@ta-interaktiv/react-article-teasers'
import { csvParse } from 'd3-dsv'

// TODO Set the correct article ID here
const ARTICLE_ID = '504246493076'

export function AppComponent() {
  const publicationDomain = parseHostname().publicationDomain
  const firstStageRef = useRef()
  const scroller = useRef(scrollama())
  const [dataForComp, setDatAForComp] = useState()
  const [newborn, setNewborn] = useState()
  const [stage, setStage] = useState({
    id: 0,
    boundingRect: {
      top: 0,
      height: 0
    }
  })

  useEffect(() => {
    const urls = [
      require('../_data/population.csv'),
      require('../_data/swiss_names_fr_2021.csv')
    ]

    // const womenData = fetch('https://interactif.24heures.ch/2022/les-prenoms-des-suisses/women_2022.csv').then(u => u.text()).then(v => csvParse(v))
    // const menData = fetch('https://interactif.24heures.ch/2022/les-prenoms-des-suisses/men_2022.csv').then(u => u.text()).then(v => csvParse(v))

    Promise.all([
      fetch(
        'https://interactif.24heures.ch/2022/les-prenoms-des-suisses/women_2022.csv'
      ),
      fetch(
        'https://interactif.24heures.ch/2022/les-prenoms-des-suisses/men_2022.csv'
      )
    ])
      .then(r => Promise.all(r.map(r => r.text())))
      .then(data => {
        setDatAForComp([urls[0], csvParse(data[0]), csvParse(data[1])])
      })

    const swissNames2019 = urls[1]
    const swissBoys = swissNames2019
      .filter(f => f.gender === 'm')
      .sort((a, b) => +b.count - +a.count)
      .map((m, i) => {
        return [m.name, +m.count, i + 1]
      })
    const swissGirls = swissNames2019
      .filter(f => f.gender === 'f')
      .sort((a, b) => +b.count - +a.count)
      .map((m, i) => {
        return [m.name, +m.count, i + 1]
      })
    setNewborn([swissBoys, swissGirls])
  }, [])

  const handleResize = () => {
    scroller.current.resize()
  }

  useEffect(() => {
    scroller.current
      .setup({
        step: '.step',
        progress: true,
        offset: 1
      })
      .onStepEnter(response => {
        setStage({
          id: +response.element.dataset.step,
          boundingRect: {
            top: response.element.getBoundingClientRect().top + window.scrollY,
            height: response.element.getBoundingClientRect().height
          }
        })
      })
      .onStepProgress(response => {
        if (response.progress >= 1) {
          setStage({
            id: +response.element.dataset.step + 0.5,
            boundingRect: {
              top:
                response.element.getBoundingClientRect().top + window.scrollY,
              height: response.element.getBoundingClientRect().height
            }
          })
        }
      })

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    const bcr = firstStageRef.current.getBoundingClientRect()
    setStage({
      id: 0,
      boundingRect: {
        top: bcr.top,
        height: bcr.height
      }
    })
  }, [firstStageRef])

  return (
    <div className='index'>
      <Masthead inverted articleId={ARTICLE_ID} />
      <ThemeSwitch />

      <ClusterCannon stage={stage} />

      <header>
        <div className='ui inverted container'>
          <div className='ui header'>
            <div className='top-line'>Le guide pour prénommer bébé</div>
            <h1>Les prénoms préférés de Suisse</h1>
            <h2>
              Combien de filles ou de garçons portent déjà ce prénom? Notre
              guide interactif vous propose d’explorer les prénoms les plus
              populaires pour les bébés et aussi pour toute la population née en
              Suisse depuis 1925.
            </h2>
          </div>
        </div>
      </header>

      <div className='step ghost one' data-step={1} ref={firstStageRef} />

      <div className='ui vertical very fitted segment'>
        <div className='ui text inverted container'>
          <MetaInfos
            authors={['Fanny Giroud', 'Mathieu Rudaz', 'Sebastian Broschinski']}
            updated='Mis à jour le 23 août 2022'
          />
          <div className='ta text'>
            <p>
              Combien de petites filles ou de petits garçons porteraient le même
              prénom que mon enfant? C’est la question que se pose tout parent à
              l’heure de faire le grand choix. Notre guide en cinq chapitres
              pour bien choisir entre popularité et originalité.
            </p>
            {/* <p>
              Chaque année, l'Office fédéral de la statistique publie la liste
              des prénoms attribués aux nouveaux-nés. Nous vous proposons, à
              travers cinq chapitres, de découvrir cette liste et d'explorer
              l'histoire fascinante du choix des prénoms suisses.
            </p> */}
          </div>
        </div>
      </div>

      <div className='ui vertical very fitted segment'>
        <div className='ui text inverted container'>
          <div className='ta text'>
            <ol className='menu'>
              <li>
                <a href='#chapitre-1'>
                  1. Les prénoms donnés aux{' '}
                  <span style={{ whiteSpace: 'nowrap' }}>
                    nouveau-nés <i className='ui icon chevron down' />
                  </span>
                </a>
              </li>
              <li>
                <a
                  href={`https://${publicationDomain}/735010740974`}
                  target='_blank'
                  rel='noreferrer'
                >
                  2. Quand l’État interdit un{' '}
                  <span style={{ whiteSpace: 'nowrap' }}>
                    prénom <span className='abo' />
                    <i className='ui icon external alternate' />
                  </span>
                </a>
              </li>
              <li>
                <a href='#chapitre-3'>
                  3. Les prénoms de la population{' '}
                  <span style={{ whiteSpace: 'nowrap' }}>
                    suisse <i className='ui icon chevron down' />
                  </span>
                </a>
              </li>
              <li>
                <a
                  href={`https://${publicationDomain}/787585221404`}
                  target='_blank'
                  rel='noreferrer'
                >
                  4. Y a-t-il des prénoms{' '}
                  <span style={{ whiteSpace: 'nowrap' }}>
                    romands? <span className='abo' />
                    <i className='ui icon external alternate' />
                  </span>
                </a>
              </li>
              <li>
                <a href='#chapitre-5'>
                  5. Quand la pop-culture s’en{' '}
                  <span style={{ whiteSpace: 'nowrap' }}>
                    mêle <i className='ui icon chevron down' />
                  </span>
                </a>
              </li>
              <li>
                <a
                  href={`https://${publicationDomain}/815042321418`}
                  target='_blank'
                  rel='noreferrer'
                >
                  6. Générez votre prénom vintage{' '}
                  <i className='ui icon external alternate' />
                </a>
              </li>
            </ol>
          </div>
        </div>
      </div>

      <div className='ui vertical very fitted segment' id='chapitre-1'>
        <div className='ui text inverted container'>
          <div className='ta text'>
            <h4>
              <div>
                <div className='number'>1</div>
              </div>
              <div>Les prénoms donnés aux nouveau-nés</div>
              <div className='line' />
            </h4>
            <p>
              Pas de révolution au royaume des prénoms: les Emma et les Gabriel
              sont en tête du palmarès des prénoms de bébés nés en 2021 en
              Suisse romande. C’était déjà le cas en 2020. Pas moins de 125
              petites filles ont été prénommées Emma dans les cantons romands,
              et chez les petits garçons 165 Gabriel ont vu le jour.
            </p>
            <p>
              Ce sont ensuite les Alice (108) et les Olivia (104) qui ont été
              les prénoms les plus populaires pour les filles. Noah (149) et
              Liam (139) complètent le trio de tête chez les garçons.
            </p>
            <p>
              Les prénoms qui ont le plus gagné en popularité sont Vera pour les
              petites filles, et Zayn chez les garçons. En revanche, les prénoms
              qui ont le plus perdu de leur attrait sont Matilda et Lionel.
            </p>
            <p>
              Deux prénoms font leur entrée dans le top 100 des prénoms les plus
              donnés aux nouveau-nés: bienvenue aux Amira et aux Emilio!
            </p>
          </div>
        </div>
      </div>

      {newborn && <Top10 data={newborn} />}

      <div className='step ghost one' data-step={2} />

      <div className='teaser ui text container '>
        <h4 style={{ marginBottom: 0 }}>
          <div className='number'>2</div>
        </h4>
        <div className='title'>Quand l’État interdit un prénom</div>
        <div className='lead'>
          Amen, Bébé, Djonathan, Lord ou Yahoo? Qui décide quels prénoms sont
          permis et lesquels sont interdits?
        </div>
        <div className='line' />
        <div className='carrot'>
          <div className='ta text inverted'>
            <p>
              En Suisse, on peut s’appeler Amen, Lord, Marvelous, Féline et même
              Obi-Wan. En revanche, il est interdit d’appeler son enfant Bébé,
              Aromat ou Ikea. Qui en décide? Où se situe la limite? Tous les
              cantons romands ne donnent pas les mêmes réponses.
            </p>
            <p>
              Un grand principe prévaut, inscrit dans le Code civil suisse: il
              ne faut pas porter préjudice à l’enfant. L’appréciation de cette
              consigne toute subjective revient aux officières et aux officiers
              d’État civil (alias le «pétabosson», dans le canton de Vaud), qui
              entrent les naissances dans le registre officiel.
            </p>
          </div>
        </div>

        <div className='ui primary button inverted'>
          <Link
            href='735010740974'
            onClick={() => {
              window.dataLayer = window.dataLayer || []
              window.dataLayer.push({
                event: 'Interactions',
                event_action: 'click',
                event_label: `href:Quand l’État interdit un prénom`
              })
            }}
          >
            Lire l'article →
          </Link>
        </div>
      </div>

      <div className='ui vertical very fitted segment' id='chapitre-3'>
        <div className='ui text inverted container'>
          <div className='ta text'>
            <h4>
              <div>
                <div className='number'>3</div>
              </div>
              <div>Les prénoms les plus populaires en Suisse</div>
              <div className='line' />
            </h4>
            <p>
              Quels sont les prénoms les plus portés dans la population? Le
              graphique ci-dessous permet de comparer l’évolution de 60’000
              prénoms au fil des ans.
            </p>
            <p>
              On y voit, pour chaque année de naissance, le nombre de personnes
              portant un prénom dans la population actuelle, par année de
              naissance.
            </p>
            <p>
              Les personnes décédées ne sont pas prises en compte, ce qui réduit
              le nombre de prénoms dans les premières années. C'est pourquoi
              nous indiquons également les valeurs relatives dans le graphique.
            </p>
            <p>
              Un exemple: 50 hommes portant le prénom Adolf sont nés en 1946 et
              vivent actuellement en Suisse. À l’époque, cela représentait 0.14%
              de la population masculine.
            </p>
          </div>
        </div>
      </div>

      <div className='ui vertical very fitted segment'>
        <div className='ui inverted container' style={{ margin: '1.5em 0' }}>
          {dataForComp && <NameComparission data={dataForComp} />}
        </div>
      </div>

      <div className='ui vertical very fitted segment'>
        <div className='ui text inverted container'>
          <div className='ui inverted small message'>
            <div className='ta text'>
              <div className='item'>
                Les prénoms qui apparaissent moins de trois fois au total ne
                sont pas affichés pour respecter la protection des données.
              </div>
              <div className='item'>
                Pour les prénoms composés de plusieurs mots, le prénom d'usage
                ou le premier prénom est pris en compte.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='step ghost one' data-step={3} />

      <div className='teaser ui text container'>
        <h4 style={{ marginBottom: 0 }}>
          <div className='number'>4</div>
        </h4>
        <div className='title'>Y a-t-il des prénoms romands?</div>
        <div className='lead'>
          Existe-t-il des prénoms à la mode en Suisse romande? Comparaisons avec
          la France et la Suisse alémanique.
        </div>
        <div className='line' />
        <div className='carrot'>
          <div className='ta text inverted'>
            <p>
              Les prénoms Agathe, Jeanne ou Romane vont-ils se répandre chez les
              petites filles suisses qui naîtront ces prochaines années ? Ils
              figurent au hit-parade des prénoms français depuis quelques
              années, mais ne se sont pas encore imposés en Suisse. En France et
              en Suisse romande, les prénoms les plus populaires sont souvent
              identiques…mais pas totalement similaires.
            </p>
            <p>
              En 20 ans, près de 60% des prénoms les plus fréquents se sont
              retrouvés des deux côtés du Jura. Mais les Romands ont leurs
              petites préférences: certains prénoms très fréquents chez nous
              sont très peu utilisés pour les bébés français.
            </p>
          </div>
        </div>
        <div className='ui primary button inverted'>
          <Link
            href='787585221404'
            onClick={() => {
              window.dataLayer = window.dataLayer || []
              window.dataLayer.push({
                event: 'Interactions',
                event_action: 'click',
                event_label: `href:Y a-t-il des prénoms romands?`
              })
            }}
          >
            Lire l'article →
          </Link>
        </div>
      </div>

      <div className='ui vertical very fitted segment' id='chapitre-5'>
        <div className='ui text inverted container'>
          <div className='ta text'>
            <h4>
              <div>
                <div className='number'>5</div>
              </div>
              <div>Quand la pop-culture s'en mêle</div>
              <div className='line' />
            </h4>
            <p>
              Chanson, télévision, pub, politique, sport... Quels événements ont
              laissé leur trace dans les statistiques des prénoms en Suisse?
              Notre sélection de prénoms célèbres et leur évolution dans le
              temps.
            </p>
          </div>
        </div>
      </div>

      <div className='ui vertical very fitted segment'>
        <div className='ui inverted container' style={{ margin: '1.5em 0' }}>
          {dataForComp && <NameInCulture data={dataForComp} />}
        </div>
      </div>

      <div className='ui vertical segment' id='chapitre-5'>
        <div className='ui inverted container'>
          <h4>Plus d’articles sur ce thème</h4>
          <ArticleTeasers
            numberOfCards='three'
            articleIds={['735010740974', '787585221404', '815042321418']}
          />
        </div>
      </div>

      <Footer
        locale='fr'
        inverted
        articleId={ARTICLE_ID}
        hidePortfolioTeaser
        mailTo='interactif@tamedia.ch'
        credits={[
          ['Textes', 'Fanny Giroud'],
          ['Graphiques', 'Sebastian Broschinski, Mathieu Rudaz'],
          ['Conception et réalisation', 'Mathieu Rudaz']
        ]}
      />
    </div>
  )
}
